import { IProfileCollectionData } from './ProfileCollectionForm';
import { AsyncStorageProvider, IAsyncSmartStorageProvider } from '../support/AsyncStorageProvider';
import { IConsentRecord, UserState } from '../user/UserState';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { AppPrincipal } from '../auth/AppPrincipal';

const PRE_AUTH_PROFILE_KEY = 'ih:pre-auth-profile';
const PRE_AUTH_CONSENTS_KEY = 'ih:pre-auth-consents';

export class PreAuthProfileService {

    private storage: IAsyncSmartStorageProvider<IProfileCollectionData>;
    private consentsStorage: IAsyncSmartStorageProvider<IConsentRecord[]>;

    static inject = () => [AsyncStorageProvider, UserState, MixPanelEventEmitter, AppPrincipal];
    constructor(
        asyncStorage: AsyncStorageProvider,
        private userState: UserState,
        private mixpanel: MixPanelEventEmitter,
        private principal: AppPrincipal
    ) {
        this.storage = asyncStorage.getJSONProvider<IProfileCollectionData>(PRE_AUTH_PROFILE_KEY, { userNeutral: true });
        this.consentsStorage = asyncStorage.getJSONProvider<IConsentRecord[]>(PRE_AUTH_CONSENTS_KEY, { userNeutral: true });
    }

    getStoredProfile = (): Promise<IProfileCollectionData | undefined> => {
        return this.storage.get();
    };

    storeProfile = (data: IProfileCollectionData): Promise<void> => {
        return this.storage.set(data);
    };

    clearStoredProfile = (): Promise<void> => {
        return this.storage.remove();
    };

    storeConsent = async (consent: IConsentRecord): Promise<void> => {
        const existing = await this.consentsStorage.get() || [];
        await this.consentsStorage.set([...existing, consent]);
    };

    applyToUserState = async ({ data, location, consent, storeToStorage }: { data: IProfileCollectionData, location: string, consent?: IConsentRecord, storeToStorage?: boolean }): Promise<void> => {
        if (!data.firstName && !data.lastName && !data.zipCode && !data.phone) {
            return;
        }

        if (storeToStorage) {
            await this.storeProfile(data);
            if (consent) {
                await this.storeConsent(consent);
            }
        }

        if (data.firstName) {
            this.userState.setFirstName(data.firstName);
        }
        if (data.lastName) {
            this.userState.setLastName(data.lastName);
        }
        if (data.zipCode) {
            this.userState.setAddress(data.zipCode);
        }
        if (data.phone) {
            this.userState.setPhone(data.phone.replace(/\D/g, ''));
        }
        if (consent) {
            this.userState.addConsent(consent);
        }

        const fieldsEntered = [
            data.firstName ? 'firstName' : '',
            data.lastName ? 'lastName' : '',
            data.zipCode ? 'zipCode' : '',
            data.phone ? 'phone' : ''
        ].filter(Boolean);

        this.mixpanel.track('Profile Collection Completed', () => ({
            Location: location,
            'Fields Entered': fieldsEntered
        }));
    };

    mergeStoredProfileOnAppStart = async (): Promise<void> => {
        const preAuthProfile = await this.getStoredProfile();
        const storedConsents = await this.consentsStorage.get();

        if (!preAuthProfile && !storedConsents) {
            return;
        }

        if (preAuthProfile) {
            if (preAuthProfile.firstName) {
                this.userState.setFirstName(preAuthProfile.firstName);
            }
            if (preAuthProfile.lastName) {
                this.userState.setLastName(preAuthProfile.lastName);
            }
            if (preAuthProfile.zipCode) {
                this.userState.setAddress(preAuthProfile.zipCode);
            }
            if (preAuthProfile.phone) {
                this.userState.setPhone(preAuthProfile.phone.replace(/\D/g, ''));
            }
        }

        if (storedConsents) {
            for (const consent of storedConsents) {
                this.userState.addConsent(consent);
            }
        }

        // only save and clear for authenticated users; anonymous users keep
        // storage so it survives reload and merges on eventual sign-up
        const isAuthenticated = this.principal.tokenType === 'user';
        if (isAuthenticated) {
            await this.userState.save();
            await this.clearStoredProfile();
            await this.consentsStorage.remove();
        }
    };
}
